<template>
  <PageContainer>
    <div class="flex flex-row place-content-around justify-between align-center gap-2">
      <div class="flex flex-col place-content-top text-left pt-4">
        <div class="">
          <span class="font-medium text-3xl mr-2">{{ formatDuration(averageDuration) }}</span>
          <span>daily average</span>
        </div>
        <div v-if="averageDuration >= targetDuration" class="text-gray-800">You're hitting your target this week!</div>
        <div v-else class="text-gray-800">
          <span>You're {{ formatDuration(targetDuration - averageDuration)  }} behind your target </span>
          <span v-if="rangeWeekly">this week.</span>
          <span v-else>in this period.</span>
        </div>
      </div>
      <MetricSleepDuration
        class="cursor-pointer"
        @click="infoDuration = true"
        :dateFrom='dateFrom'
        :dateTo='dateTo'
        :hideLabel='true'
        :hideName='true'
      />
    </div>
    <div class="relative w-full h-72 mt-2">
      <div v-if="loading" class="absolute flex flex-col items-center justify-center top-0 -bottom-12 w-full bg-white z-10">
        <object class="h-8 w-auto pointer-events-none animate-ping" :data="logoImage" type="image/svg+xml"></object>
        <p class="mt-4 text-gray-400">Loading...</p>
      </div>
      <TimeseriesGraph
        v-if='graphDuration'
        :dataset='dataDuration'
        :stepSize='120'
        :formatter='formatDuration'
        :targetLevels='[targetDuration]'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if='graphOpportunity'
        :dataset='dataOpportunity'
        :stepSize='120'
        :formatter='formatDuration'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if='graphEfficiency'
        :dataset='dataEfficiency'
        :stepSize='0.2'
        :formatter='formatPercentage'
        :targetLevels='[0.85]'
        :line='true'
        :startAtZero='true'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if='graphLatency'
        :dataset='dataLatency'
        :stepSize='5'
        :formatter='formatDuration'
        :dateFormat='dateFormat'
      />
    </div>
    <!-- TODO: Yearly graph view. -->
    <div class="grid grid-cols-3 gap-2 w-full bg-gray-50 rounded-lg mt-2">
      <div @click="setRangeWeekly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeWeekly }">7d</div>
      <div @click="setRangeMonthly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeMonthly }">4w</div>
      <div @click="setRangeHalfly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeHalfly }">6m</div>
      <!-- <div @click="setRangeYearly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeYearly }">1y</div> -->
    </div>
    <!-- TODO: On-hover for the buttons. -->
    <div class="-mx-4 overflow-x-auto">
      <div class="flex flex-row gap-2 w-full mt-3 mb-8 mx-4 w-max">
        <div @click="setGraphDuration()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphDuration }">Duration</div>
        <div @click="setGraphOpportunity()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphOpportunity }">Opportunity</div>
        <div @click="setGraphEfficiency()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphEfficiency }">Efficiency</div>
        <div @click="setGraphLatency()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphLatency }">Latency</div>
      </div>
    </div>
    <div class="mt-4 text-left">
      <!-- <h2>Duration & opportunity</h2> -->
      <div class="grid grid-cols-2 gap-4 justify-between">
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatDuration(averageDuration) }}</div>
          <div>Average duration</div>
        </div>
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatDuration(averageOpportunity) }}</div>
          <div>Average opportunity</div>
        </div>
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatPercentage(averageEfficiency) }}</div>
          <div>Average efficiency</div>
        </div>
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatDuration(averageLatency) }}</div>
          <div>Average latency</div>
        </div>
      </div>
    </div>
  </PageContainer>

  <!-- MODALS -->
  <InformationDuration
    v-if="infoDuration"
    v-model="infoDuration"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue'
import PageContainer from '@/components/PageContainer'
import MetricSleepDuration from '@/components/MetricSleepDuration'
import TimeseriesGraph from '@/components/TimeseriesGraph'
import { isSameDate, formatDuration } from '@/helpers/time'
import User from '@/models/user'
import { mapStores } from 'pinia'
import { useSleepStore } from '@/stores/sleep'
import { useUserStore } from '@/stores/user'

const GRAPH_DURATION = 0
const GRAPH_OPPORTUNITY = 1
const GRAPH_EFFICIENCY = 2
const GRAPH_LATENCY = 3

const RANGE_DAYS_7 = 7 - 1
const RANGE_WEEKS_4 = 7 * 4 - 1
const RANGE_MONTHS_6 = 7 * 4 * 6 - 1
const RANGE_YEARS_1 = 367 - 1

export default {
  name: 'DataDuration',
  components: {
    PageContainer,
    MetricSleepDuration,
    TimeseriesGraph,
    InformationDuration: defineAsyncComponent(
      () => import('@/components/modals/InformationDuration'),
    ),
  },
  data() {
    let d = new Date()
    d.setDate(d.getDate() - RANGE_DAYS_7)

    return {
      graph: GRAPH_DURATION,
      range: RANGE_DAYS_7,
      loading: true,
      dateFrom: d.format(),
      dateTo: new Date().format(),
      data: undefined,
      targetDuration: undefined,
      logoImage: require('@/assets/logo.svg'),
      infoDuration: false,
    }
  },
  beforeMount() {
    this.loadSummary()
    this.loadSleeps()
    this.loadTarget()
  },
  watch: {
    // TODO: Also watch `dateTo` when implementing date range picker.
    dateFrom() {
      this.loadSleeps()
    },
  },
  methods: {
    async loadSummary() {
      await this.userStore.user.getSleepDuration(this.dateFrom, this.dateTo)
    },
    async loadSleeps() {
      this.loading = true
      const sleeps = await this.userStore.user.getSleeps(this.dateFrom, this.dateTo, true)
      let data = []
      for (const sleep of sleeps) {
        data.push({
          date: sleep.date,
          duration: sleep.duration,
          opportunity: await sleep.opportunity,
          efficiency: await sleep.efficiency,
          latency: await sleep.latency,
        })
      }
      // Pad array so its length is in the range.
      this.dataLength = data.length
      data = this.padArray(data, this.range + 1)
      if (data.length > RANGE_WEEKS_4 + 1) {
        data = this.monthlyAggregate(data)
      }


      // TODO: Sort by date.
      this.data = data.sort()
      this.loading = false
    },
    async loadTarget() {
      User.getCurrentUser()
      .then(user => {
        // this.user = user
        return user.getSleepTarget()
      })
      .then(target => {
        this.targetDuration = target.duration
      })
    },
    padArray(arr, len) {
      if (arr.length === len) {
        return arr
      }

      // Pad array with date but empty datapoint field.
      let d = new Date(this.dateFrom)

      // Start with an empty date array.
      let empty = Array(len).fill({})
      const fill = empty.map((element) => {
        element = {
          date: new Date(d),
          duration: null,
          opportunity: null,
          efficiency: null,
          latency: null,
        }
        d.setDate(d.getDate() + 1)
        return element
      })

      // Go through existing datapoints and fill where date matches.
      return fill.map((element) => {
        const match = arr?.find(e => isSameDate(e.date, element.date))
        if (match) {
          return {...element, ...match}
        } else {
          return element
        }
      })
    },
    monthlyAggregate(arr) {
      let result = []
      const reverse = arr.toReversed()
      for (let i = 0; i < reverse.length; i++) {
        let month = []
        const currentMonth = reverse[reverse.length - 1]?.date.getMonth()
        while (currentMonth === reverse[reverse.length - 1]?.date.getMonth()) {
          month.push(reverse.pop())
        }
        result.push({
          date: month[0].date,
          duration: this.average(month.map(d => d.duration)),
          opportunity: this.average(month.map(d => d.opportunity)),
          efficiency: this.average(month.map(d => d.efficiency)),
          latency: this.average(month.map(d => d.latency)),
        })
      }
      return result
    },
    formatDuration(value) {
      return value === null || isNaN(value) ? '' : formatDuration(value / 60)
    },
    formatPercentage(value) {
      return (!value && value != 0) ? '' : Math.round(value * 100) + '%'
    },
    average(array) {
      const nonNullLength = array?.filter(x => x != null).length
      if (array?.length === 0 || nonNullLength === 0) return null
      return array?.reduce((a, b) => a + b) / nonNullLength
    },
    setGraphDuration() {
      this.graph = GRAPH_DURATION
    },
    setGraphOpportunity() {
      this.graph = GRAPH_OPPORTUNITY
    },
    setGraphEfficiency() {
      this.graph = GRAPH_EFFICIENCY
    },
    setGraphLatency() {
      this.graph = GRAPH_LATENCY
    },
    setRangeWeekly() {
      this.range = RANGE_DAYS_7
      let d = new Date()
      d.setDate(d.getDate() - RANGE_DAYS_7)
      this.dateFrom = d.format()
    },
    setRangeMonthly() {
      this.range = RANGE_WEEKS_4
      let d = new Date()
      d.setDate(d.getDate() - RANGE_WEEKS_4)
      this.dateFrom = d.format()
    },
    setRangeHalfly() {
      this.range = RANGE_MONTHS_6
      let d = new Date()
      d.setDate(d.getDate() - RANGE_MONTHS_6)
      this.dateFrom = d.format()
    },
    setRangeYearly() {
      this.range = RANGE_YEARS_1
      let d = new Date()
      d.setDate(d.getDate() - RANGE_YEARS_1)
      this.dateFrom = d.format()
    },
  },
  computed: {
    graphDuration() {
      return this.graph === GRAPH_DURATION
    },
    graphOpportunity() {
      return this.graph === GRAPH_OPPORTUNITY
    },
    graphEfficiency() {
      return this.graph === GRAPH_EFFICIENCY
    },
    graphLatency() {
      return this.graph === GRAPH_LATENCY
    },
    dataDuration() {
      return this.data?.map(d => {
        return {
          date: d.date,
          datapoint: d.duration !== undefined ? d.duration : null
        }
      })
    },
    dataOpportunity() {
      return this.data?.map(d => {
        return {
          date: d.date,
          datapoint: d.opportunity !== undefined ? d.opportunity : null
        }
      })
    },
    dataEfficiency() {
      return this.data?.map(d => {
        return {
          date: d.date,
          datapoint: d.efficiency !== undefined ? d.efficiency : null
        }
      })
    },
    dataLatency() {
      return this.data?.map(d => {
        return {
          date: d.date,
          datapoint: d.latency !== undefined ? d.latency : null
        }
      })
    },
    averageDuration() {
      const data = this.dataDuration?.map(x => x.datapoint)
      return data ? this.average(data) : null
    },
    averageOpportunity() {
      const data = this.dataOpportunity?.map(x => x.datapoint)
      return data ? this.average(data) : null
    },
    averageEfficiency() {
      const data = this.dataEfficiency?.map(x => x.datapoint)
      return data ? this.average(data) : null
    },
    averageLatency() {
      const data = this.dataLatency?.map(x => x.datapoint)
      return data ? this.average(data) : null
    },
    rangeWeekly() {
      return this.range === RANGE_DAYS_7
    },
    rangeMonthly() {
      return this.range === RANGE_WEEKS_4
    },
    rangeHalfly() {
      return this.range === RANGE_MONTHS_6
    },
    rangeYearly() {
      return this.range === RANGE_YEARS_1
    },
    dateFormat() {
      switch (this.range) {
        case RANGE_DAYS_7:
          return { weekday: 'short' }
        case RANGE_WEEKS_4:
          return { month: 'short', day: 'numeric' }
        case RANGE_MONTHS_6:
          return { month: 'short' }
        case RANGE_YEARS_1:
          return { month: 'short' }
        default:
          return null
      }
    },
    ...mapStores(useSleepStore, useUserStore),
  },
}
</script>